import { isDefined } from "@clipboard-health/util-ts";
import { useTheme } from "@mui/material";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { CircleProgress, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { startOfSecond } from "date-fns";

import { ShiftCircleProgressContent } from "./Content";
import { getShiftProgress } from "./utils/getShiftProgress";

interface ShiftCircleProgressProps {
  startDate: Date;
  endDate: Date;
  clockInDate?: Date;
  includeDatePill?: boolean;
  clockOutModalState: UseModalState;
}

export function ShiftCircleProgress(props: ShiftCircleProgressProps) {
  const { startDate, endDate, clockInDate, includeDatePill = false, clockOutModalState } = props;

  const currentDate = useCurrentDate(1000);
  const theme = useTheme();

  if (!isDefined(clockInDate)) {
    return null;
  }

  const now = startOfSecond(currentDate);

  const {
    progress,
    secondaryProgress,
    timeWorked,
    timeLeft,
    isOvertime,
    overtimeText,
    totalTimeWorked,
  } = getShiftProgress({
    clockInDate,
    shiftStartDate: startDate,
    shiftEndDate: endDate,
    now,
  });

  const hasExceededOvertimeThreshold = isOvertime && progress >= 1;

  return (
    <CircleProgress
      value={progress}
      color={
        hasExceededOvertimeThreshold
          ? theme.palette.warning.main
          : theme.palette.components?.circleProgress.primary
      }
      secondaryValue={isOvertime ? secondaryProgress : undefined}
      secondaryColor={theme.palette.components?.circleProgress.primary}
    >
      <ShiftCircleProgressContent
        hasExceededOvertimeThreshold={hasExceededOvertimeThreshold}
        includeDatePill={includeDatePill}
        shiftStartDate={startDate}
        isOvertime={isOvertime}
        progress={progress}
        overtimeText={overtimeText}
        totalTimeWorked={totalTimeWorked}
        timeWorked={timeWorked}
        timeLeft={timeLeft}
        clockOutModalState={clockOutModalState}
      />
    </CircleProgress>
  );
}
